/*
 * OrbitLiveVoice — a real-time spoken conversation with Orbit; the report is
 * written at the end.
 *
 * The conversation itself runs on the ElevenLabs real-time agent (low latency,
 * natural turn-taking, barge-in) so it FEELS like talking to someone. We
 * deliberately do NOT generate per-turn — that is slow and would break the
 * rhythm. The browser captures the whole transcript instead, and on disconnect
 * POSTs it to /voice/web/finalize, which runs the same pipeline the typed chat
 * runs (extract → create/scope the problem → draft the PRD) once, at the end.
 *
 * Capturing here also sidesteps reachability: ElevenLabs never has to call our
 * backend for the session to produce anything — the browser relays it.
 *
 * The three ways in live together at the bottom of the intake: this component
 * is voice (the default), `onTypeInstead` reveals the composer, and
 * `onScheduleCall` hands off to the "Orbit calls me" modal.
 *
 * The SDK requires useConversation() inside a <ConversationProvider>, so the
 * export wraps an inner component that owns the hook.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AudioLines, Ear, Loader2, Mic, Phone, PhoneOff } from 'lucide-react';
import { ConversationProvider, useConversation } from '@elevenlabs/react';

import {
  finalizeWebVoiceSession, startWebVoiceSession, transcribeAudio,
  type WebVoiceOutcome,
} from '../lib/intakeApi';

interface OrbitLiveVoiceProps {
  clientId?: string | null;
  /** When set, the session resumes/scopes this problem. */
  problemId?: string | null;
  /** Fired once the session has been written up. Carries the outcome — the
   *  problem it produced and whether a PRD is drafting — so the host can send
   *  the user on instead of leaving them on a finished conversation. */
  onEnded?: (outcome: WebVoiceOutcome | null) => void;
  /** Reveals the composer — voice is the default, typing is the second way. */
  onTypeInstead?: () => void;
  /** Opens the "Orbit calls me" scheduler — the third way. */
  onScheduleCall?: () => void;
  /** Connect on mount so Orbit opens the conversation without a click. */
  autoStart?: boolean;
  /** Each spoken line, so a host can mirror the conversation elsewhere. */
  onTranscriptTurn?: (role: 'user' | 'orbit', text: string) => void;
  disabled?: boolean;
}

const MIC_SUPPORTED =
  typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

function LiveVoiceInner({
  clientId, problemId, onEnded, onTypeInstead, onScheduleCall,
  autoStart, onTranscriptTurn, disabled,
}: OrbitLiveVoiceProps) {
  const [starting, setStarting] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [ended, setEnded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Every spoken line stays on screen as a chat message, so the conversation
  // reads back exactly like the typed one.
  const [messages, setMessages] = useState<Array<{ id: number; role: 'user' | 'orbit'; text: string }>>([]);
  const msgIdRef = useRef(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const callTokenRef = useRef<string | null>(null);
  const transcriptRef = useRef<Array<{ role: string; text: string }>>([]);
  // Callbacks through refs: the conversation handlers are created once, and a
  // stale closure here would finalize against the wrong session.
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;
  const onTurnRef = useRef(onTranscriptTurn);
  onTurnRef.current = onTranscriptTurn;

  // A second, independent capture of the user's own mic for the whole session,
  // transcribed once at the end. The SDK's onMessage is the only source for
  // Orbit's side, but it is not something we control for the user's — and the
  // transcript IS the report's input, so it gets a belt and braces.
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recStreamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const startRecording = useCallback(async () => {
    if (typeof MediaRecorder === 'undefined') return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recStreamRef.current = stream;
      const mime =
        MediaRecorder.isTypeSupported?.('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported?.('audio/webm') ? 'audio/webm'
        : '';
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.start();
      recorderRef.current = rec;
    } catch (e) {
      console.warn('[orbit-voice] mic recording unavailable', e);
    }
  }, []);

  const stopRecordingAndTranscribe = useCallback(async (): Promise<string> => {
    const rec = recorderRef.current;
    recorderRef.current = null;
    const releaseMic = () => {
      try { recStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
      recStreamRef.current = null;
    };
    if (!rec) { releaseMic(); return ''; }
    const blob: Blob = await new Promise((resolve) => {
      rec.onstop = () => resolve(new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' }));
      try {
        if (rec.state !== 'inactive') rec.stop();
        else resolve(new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' }));
      } catch { resolve(new Blob(chunksRef.current, { type: 'audio/webm' })); }
    });
    releaseMic();
    chunksRef.current = [];
    if (blob.size < 1200) return '';   // too short to be speech
    try {
      return (await transcribeAudio(blob)) || '';
    } catch (e) {
      console.warn('[orbit-voice] transcription failed', e);
      return '';
    }
  }, []);

  const conversation = useConversation({
    onConnect: () => { console.info('[orbit-voice] connected'); },
    onMessage: (m: { message?: string; source?: 'user' | 'ai' }) => {
      const text = (m?.message || '').trim();
      if (!text) return;
      const role: 'user' | 'orbit' = m?.source === 'user' ? 'user' : 'orbit';
      transcriptRef.current.push({ role: role === 'user' ? 'User' : 'Orbit', text });
      msgIdRef.current += 1;
      setMessages((prev) => [...prev, { id: msgIdRef.current, role, text }]);
      onTurnRef.current?.(role, text);
    },
    onError: (msg: unknown) => {
      console.error('[orbit-voice] error', msg);
      setError('Voice connection dropped. Try again, or type instead.');
    },
    onDisconnect: (details: unknown) => {
      console.info('[orbit-voice] disconnected', details);
      setEnded(true);
      const token = callTokenRef.current;
      callTokenRef.current = null;
      const spoken = transcriptRef.current.map((t) => `${t.role}: ${t.text}`).join('\n');
      transcriptRef.current = [];
      void (async () => {
        setFinalizing(true);
        let outcome: WebVoiceOutcome | null = null;
        try {
          const recorded = (await stopRecordingAndTranscribe()).trim();
          const parts: string[] = [];
          if (spoken.trim()) parts.push(spoken.trim());
          if (recorded && recorded !== spoken.trim()) parts.push(`User (transcribed): ${recorded}`);
          const transcript = parts.join('\n\n');
          if (token) outcome = await finalizeWebVoiceSession(token, transcript);
        } catch (e) {
          console.warn('[orbit-voice] finalize failed', e);
        } finally {
          setFinalizing(false);
          onEndedRef.current?.(outcome);
        }
      })();
    },
  });

  const status = conversation.status;   // disconnected | connecting | connected | error
  const connected = status === 'connected';
  const connecting = starting || status === 'connecting';

  const start = useCallback(async () => {
    if (connecting || connected || finalizing) return;
    setError(null);
    setEnded(false);
    setMessages([]);
    setStarting(true);
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      const session = await startWebVoiceSession({
        problemId: problemId ?? undefined,
        clientId: clientId ?? undefined,
      });
      callTokenRef.current = session.call_token;
      transcriptRef.current = [];
      void startRecording();
      const common = {
        dynamicVariables: session.dynamic_variables,
        overrides: session.first_message
          ? { agent: { firstMessage: session.first_message } }
          : undefined,
      };
      if (session.conversation_token) {
        await conversation.startSession({
          conversationToken: session.conversation_token,
          connectionType: 'webrtc',
          ...common,
        });
      } else if (session.signed_url) {
        await conversation.startSession({
          signedUrl: session.signed_url,
          connectionType: 'websocket',
          ...common,
        });
      } else {
        throw new Error('No voice transport was returned.');
      }
    } catch (e: any) {
      console.error('[orbit-voice] start failed', e);
      setError(
        e?.name === 'NotAllowedError' || e?.name === 'NotFoundError'
          ? 'Microphone access is needed to talk to Orbit.'
          : (e?.response?.data?.detail || 'Could not start the voice session.'),
      );
    } finally {
      setStarting(false);
    }
  }, [connecting, connected, finalizing, problemId, clientId, conversation, startRecording]);

  const stop = useCallback(() => {
    try { conversation.endSession(); }
    catch (e) { console.error('[orbit-voice] end failed', e); }
  }, [conversation]);

  // Voice-first: connect shortly after mount so Orbit greets and starts, with
  // no "Talk to Orbit" click. The timeout plus its cleanup makes it fire
  // EXACTLY once through StrictMode's mount→unmount→remount in dev. A blocked
  // mic lands in the error state, which offers a retry and the other two ways.
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (!autoStart || autoStartedRef.current || disabled) return;
    const id = window.setTimeout(() => {
      autoStartedRef.current = true;
      void start();
    }, 350);
    return () => window.clearTimeout(id);
  }, [autoStart, disabled, start]);

  // End the session only on TRUE unmount (the user switched to typing). The ref
  // holds the latest endSession so this effect doesn't re-run — and kill a live
  // conversation — every time the conversation state changes.
  const endRef = useRef<() => void>(() => {});
  endRef.current = () => {
    try { conversation.endSession(); } catch { /* ignore */ }
    try { if (recorderRef.current?.state !== 'inactive') recorderRef.current?.stop(); } catch { /* ignore */ }
    try { recStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    recorderRef.current = null;
    recStreamRef.current = null;
  };
  useEffect(() => () => { endRef.current(); }, []);

  if (!MIC_SUPPORTED) return null;

  const otherWays = (
    <>
      {onTypeInstead && (
        <button type="button" className="aa-livevoice-alt" onClick={onTypeInstead}>
          Type instead
        </button>
      )}
      {onScheduleCall && (
        <button type="button" className="aa-livevoice-alt" onClick={onScheduleCall}>
          <Phone size={13} strokeWidth={2.25} /> Have Orbit call me
        </button>
      )}
    </>
  );

  let control: React.ReactNode;
  if (finalizing) {
    control = (
      <div className="aa-livevoice aa-livevoice--live" role="status" aria-live="polite">
        <span className="aa-livevoice-orb"><Loader2 size={14} strokeWidth={2.5} className="aa-livevoice-spin" /></span>
        <span className="aa-livevoice-label">Wrapping up — preparing your report…</span>
      </div>
    );
  } else if (connected) {
    const speaking = conversation.isSpeaking;
    control = (
      <div className="aa-livevoice aa-livevoice--live" role="status" aria-live="polite">
        <span className={`aa-livevoice-orb${speaking ? ' is-speaking' : ''}`}>
          {speaking ? <AudioLines size={14} strokeWidth={2.5} /> : <Ear size={14} strokeWidth={2.5} />}
        </span>
        <span className="aa-livevoice-label">{speaking ? 'Orbit is speaking…' : 'Listening…'}</span>
        {/* Switching to text stays available mid-conversation. */}
        {otherWays}
        <button
          type="button"
          className="aa-livevoice-end"
          onClick={stop}
          aria-label="End voice conversation"
        >
          <PhoneOff size={14} strokeWidth={2.5} /> End
        </button>
      </div>
    );
  } else if (error) {
    control = (
      <div className="aa-livevoice aa-livevoice--intro">
        <span className="aa-livevoice-err">{error}</span>
        <button
          type="button"
          className="aa-livevoice-start"
          onClick={start}
          disabled={disabled || connecting}
          aria-label="Try voice again"
        >
          {connecting ? <Loader2 size={16} className="aa-livevoice-spin" /> : <Mic size={16} strokeWidth={2.5} />}
          {connecting ? 'Connecting…' : 'Try voice again'}
        </button>
        {otherWays}
      </div>
    );
  } else if (connecting || (autoStart && !ended)) {
    control = (
      <div className="aa-livevoice aa-livevoice--intro">
        <span className="aa-livevoice-orb"><Loader2 size={14} strokeWidth={2.5} className="aa-livevoice-spin" /></span>
        <span className="aa-livevoice-label">Connecting to Orbit…</span>
        {otherWays}
      </div>
    );
  } else {
    control = (
      <div className="aa-livevoice aa-livevoice--intro">
        <button
          type="button"
          className="aa-livevoice-start"
          onClick={start}
          disabled={disabled}
          aria-label="Talk to Orbit"
        >
          <Mic size={16} strokeWidth={2.5} /> Talk to Orbit
        </button>
        {otherWays}
      </div>
    );
  }

  return (
    <div className="aa-livevoice-wrap">
      {messages.length > 0 && (
        <div className="aa-livevoice-transcript" ref={scrollRef}>
          {messages.map((m) => (
            <div key={m.id} className={`aa-livevoice-row aa-livevoice-row--${m.role}`}>
              <div className="aa-livevoice-bubble">
                <span className="aa-livevoice-who">{m.role === 'user' ? 'You' : 'Orbit'}</span>
                {m.text}
              </div>
            </div>
          ))}
        </div>
      )}
      {control}
    </div>
  );
}

export function OrbitLiveVoice(props: OrbitLiveVoiceProps) {
  if (!MIC_SUPPORTED) return null;
  return (
    <ConversationProvider>
      <LiveVoiceInner {...props} />
    </ConversationProvider>
  );
}

export default OrbitLiveVoice;
